import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { MarketRunners } from '@/store/api/runnersApi';

export type ResultValue = {
  status: 'Winner' | 'Looser' | 'None';
  selectionId: string;
};

interface UseResultModalProps {
  open: boolean;
  marketType?: string;
  runners: MarketRunners;
  marketName?: string;
  onClose: () => void;
  onSave: (result: Record<string, ResultValue>, runs: string) => void;
}

export const useResultModal = ({
  open,
  marketType,
  marketName,
  runners,
  onClose,
  onSave,
}: UseResultModalProps) => {
  const [results, setResults] = useState<Record<string, ResultValue>>({});
  const [runs, setRuns] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);

  const { runner } = runners ?? {};
  const teams = Array.isArray(runner) ? runner.map(({ runnerName }) => runnerName) : [];

  const getSelectionId = (teamName: string) => {
    const r = runner?.find((r) => r.runnerName === teamName);
    return r?.selectionId || r?.runnerId || '';
  };

  useEffect(() => {
    if (open) {
      setResults({});
      setRuns('');
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (modalRef.current?.contains(target)) return;
      if (target.closest('[data-result-confirm-modal="true"]')) return;
      const isSelectOpen = document.querySelector('[data-state="open"]');
      if (isSelectOpen) return;
      onClose();
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose]);

  const handleSelect = (team: string, value: 'Winner' | 'Looser' | 'None') => {
    setResults((prev) => {
      const updated: Record<string, ResultValue> = {};

      teams.forEach((t) => {
        updated[t] = {
          status: prev[t]?.status ?? 'None',
          selectionId: getSelectionId(t),
        };
      });

      if (value === 'Winner') {
        teams.forEach((t) => {
          updated[t] = {
            status: t === team ? 'Winner' : 'Looser',
            selectionId: getSelectionId(t),
          };
        });
      } else if (value === 'Looser') {
        teams.forEach((t) => {
          updated[t] = {
            status: t === team ? 'Looser' : 'None',
            selectionId: getSelectionId(t),
          };
        });
      } else {
        updated[team] = {
          status: 'None',
          selectionId: getSelectionId(team),
        };
      }

      return updated;
    });
  };

  const handleSave = () => {
    if (marketType === 'fancy' && marketName?.toLowerCase() === 'session') {
      if (!runs.trim()) {
        toast.error('Please enter runs for fancy market');
        return false;
      }
    } else if (marketType === 'fancy' && marketName?.toLowerCase() !== 'session') {
      const hasResult = Object.values(results).some(
        (r) => r.status === 'Winner' || r.status === 'Looser'
      );
      if (!hasResult) {
        toast.error('Please enter result');
        return false;
      }
    } else {
      const hasWinner = Object.values(results).some((r) => r.status === 'Winner');
      if (!hasWinner) {
        toast.error('Please select at least one winner');
        return false;
      }
    }

    return true;
  };

  const confirmSave = () => {
    onSave(results, runs);
  };

  return {
    modalRef,
    teams,
    results,
    runs,
    setRuns,
    handleSelect,
    handleSave,
    confirmSave,
  };
};
